import { useState } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';

/**
 * Book data from the backend.
 */
interface TomeBook {
    id: number;
    title: string;
    slug: string;
}

/**
 * Props for the Create page.
 */
interface Props {
    book: TomeBook;
}

/**
 * GM Tome Chapter Create page - form for creating a new chapter within a book.
 */
export default function Create({ book }: Props) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        router.post(`/gm/tome/books/${book.id}/chapters`, {
            title,
            description: description || null,
        }, {
            onError: (errors) => {
                setErrors(errors);
                setIsSubmitting(false);
            },
            onSuccess: () => {
                setIsSubmitting(false);
            },
        });
    };

    return (
        <GmLayout title={`Create Chapter - ${book.title}`} activePath="/gm/tome/books">
            <div className="max-w-2xl">
                <div className="flex items-center gap-4 mb-6">
                    <Link
                        href={`/gm/tome/books/${book.id}`}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        ← Back to {book.title}
                    </Link>
                </div>

                <h1 className="text-2xl font-bold text-white mb-2">📑 Create New Chapter</h1>
                <p className="text-gray-400 mb-6">Adding chapter to: <span className="text-white">{book.title}</span></p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Title <span className="text-red-400">*</span>
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                            placeholder="Enter chapter title"
                            required
                        />
                        {errors.title && <p className="mt-1 text-sm text-red-400">{errors.title}</p>}
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                            placeholder="Optional description for the chapter"
                        />
                        {errors.description && <p className="mt-1 text-sm text-red-400">{errors.description}</p>}
                    </div>

                    {/* Submit Button */}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-not-allowed text-white rounded transition-colors"
                        >
                            {isSubmitting ? 'Creating...' : 'Create Chapter'}
                        </button>
                        <Link
                            href={`/gm/tome/books/${book.id}`}
                            className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>
        </GmLayout>
    );
}

